import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Player, PlayerBadgesData } from '../types';
import { BADGES_CONFIG, BadgeItem, ChemistryCategory } from '../config/badgesConfig';
import './PlayerBadges.css';

interface PlayerBadgesProps {
  player: Player;
  isAdmin: boolean;
  onSave: (updatedBadges: PlayerBadgesData) => Promise<void>;
}

const BadgeTooltipPortal: React.FC<{
  anchorRef: React.RefObject<HTMLElement | null>;
  show: boolean;
  children: React.ReactNode;
}> = ({ anchorRef, show, children }) => {
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    if (!show || !anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPos({ x: rect.left + rect.width / 2, y: rect.top });
  }, [show, anchorRef]);

  if (!show) return null;

  return createPortal(
    <div
      className="player-badge-tooltip player-badge-tooltip-portal"
      style={{ left: pos.x, top: pos.y }}
      role="tooltip"
    >
      {children}
    </div>,
    document.body
  );
};

const BadgeSlot: React.FC<{
  info: BadgeItem | null;
  onSelect: (id: string) => void;
  onRemove: () => void;
  items: BadgeItem[];
  isAdmin: boolean;
  tooltipText?: string;
  slotLabel: string;
}> = ({ info, onSelect, onRemove, items, isAdmin, tooltipText, slotLabel }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const displayText = tooltipText || info?.name || '';

  return (
    <div className="player-badge-slot">
      {info ? (
        <div
          ref={anchorRef}
          className="player-badge-item"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onClick={() => setShowTooltip((v) => !v)}
        >
          <img src={info.image} alt={info.name} />
          {isAdmin && (
            <button type="button" className="player-badge-remove" onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Kaldır">×</button>
          )}
          <BadgeTooltipPortal anchorRef={anchorRef} show={showTooltip && !!displayText}>
            {displayText}
          </BadgeTooltipPortal>
        </div>
      ) : isAdmin ? (
        <div className="player-badge-slot-add">
          <button type="button" className="player-badges-add-btn" onClick={() => setShowPicker(!showPicker)} title={`${slotLabel} ekle`}>+</button>
          {showPicker && (
            <>
              <div className="player-badges-overlay" onClick={() => setShowPicker(false)} />
              <div className="player-badges-picker">
                <button type="button" className="player-badges-back" onClick={() => setShowPicker(false)}>← Geri</button>
                {items.length === 0 ? (
                  <p className="player-badges-empty">Henüz eklenmemiş.</p>
                ) : (
                  items.map(item => (
                    <button key={item.id} type="button" className="player-badges-picker-item" onClick={() => { onSelect(item.id); setShowPicker(false); }}>
                      <img src={item.image} alt={item.name} />
                      <span>{item.name}</span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="player-badge-slot-empty" />
      )}
    </div>
  );
};

const CHEMISTRY_CATEGORIES: ChemistryCategory[] = ['HÜCUM', 'ORTA SAHA', 'DEFANS', 'ÖZEL', 'KALECİ'];

export const PlayerBadges: React.FC<PlayerBadgesProps> = ({ player, isAdmin, onSave }) => {
  const [showStylePicker, setShowStylePicker] = useState(false);
  const [showChemistryPicker, setShowChemistryPicker] = useState(false);

  const badges: PlayerBadgesData = player.badges || { styles: [], chemistries: [] };
  const { country, league, team, styles = [], chemistries = [] } = badges;

  const countryInfo = country ? BADGES_CONFIG.countries.find(c => c.id === country) : null;
  const leagueInfo = league ? BADGES_CONFIG.leagues.find(l => l.id === league) : null;
  const teamInfo = team ? BADGES_CONFIG.teams.find(t => t.id === team) : null;

  const handleSet = async (key: keyof PlayerBadgesData, value: string | string[] | undefined) => {
    await onSave({ ...badges, [key]: value });
  };

  const handleAddStyle = async (id: string) => {
    if (styles.includes(id)) return;
    await onSave({ ...badges, styles: [...styles, id] });
    setShowStylePicker(false);
  };

  const handleRemoveStyle = async (id: string) => {
    await onSave({ ...badges, styles: styles.filter(s => s !== id) });
  };

  const handleAddChemistry = async (id: string) => {
    if (chemistries.includes(id)) return;
    await onSave({ ...badges, chemistries: [...chemistries, id] });
    setShowChemistryPicker(false);
  };

  const handleRemoveChemistry = async (id: string) => {
    await onSave({ ...badges, chemistries: chemistries.filter(c => c !== id) });
  };

  const availableChemistries = BADGES_CONFIG.chemistries.filter(c => !chemistries.includes(c.id));

  return (
    <div className="player-badges">
      {/* Satır 1: Ülke, lig, takım + kimyalar */}
      <div className="player-badges-row player-badges-row-1">
        <BadgeSlot
          info={countryInfo || null}
          slotLabel="Ülke"
          onSelect={(id) => handleSet('country', id)}
          onRemove={() => handleSet('country', undefined)}
          items={BADGES_CONFIG.countries}
          isAdmin={isAdmin}
        />
        <BadgeSlot
          info={leagueInfo || null}
          slotLabel="Lig"
          onSelect={(id) => handleSet('league', id)}
          onRemove={() => handleSet('league', undefined)}
          items={BADGES_CONFIG.leagues}
          isAdmin={isAdmin}
        />
        <BadgeSlot
          info={teamInfo || null}
          slotLabel="Takım"
          onSelect={(id) => handleSet('team', id)}
          onRemove={() => handleSet('team', undefined)}
          items={BADGES_CONFIG.teams}
          isAdmin={isAdmin}
        />
        {chemistries.map(chemistryId => {
          const chemistryInfo = BADGES_CONFIG.chemistries.find(c => c.id === chemistryId);
          if (!chemistryInfo) return null;
          return (
            <ChemistryBadge
              key={chemistryId}
              info={chemistryInfo}
              isAdmin={isAdmin}
              onRemove={() => handleRemoveChemistry(chemistryId)}
            />
          );
        })}
        {isAdmin && (
          <div className="player-badges-style-add">
            <button
              type="button"
              className="player-badges-add-btn"
              onClick={() => setShowChemistryPicker(!showChemistryPicker)}
              title="Kimya ekle"
            >
              +
            </button>
            {showChemistryPicker && (
              <>
                <div className="player-badges-overlay" onClick={() => setShowChemistryPicker(false)} />
                <div className="player-badges-picker player-badges-picker-chemistries">
                  <button type="button" className="player-badges-back" onClick={() => setShowChemistryPicker(false)}>
                    ← Geri
                  </button>
                  {BADGES_CONFIG.chemistries.length === 0 ? (
                    <p className="player-badges-empty">Henüz eklenmemiş.</p>
                  ) : availableChemistries.length === 0 ? (
                    <p className="player-badges-empty">Tüm kimyalar eklendi.</p>
                  ) : (
                    CHEMISTRY_CATEGORIES.map(category => {
                      const items = availableChemistries.filter(c => c.kategori === category);
                      if (items.length === 0) return null;
                      return (
                        <div key={category} className="player-badges-picker-group">
                          <p className="player-badges-picker-group-title">{category}</p>
                          {items.map(item => (
                            <button
                              key={item.id}
                              type="button"
                              className="player-badges-picker-item"
                              onClick={() => handleAddChemistry(item.id)}
                            >
                              <img src={item.image} alt={item.name} />
                              <span>{item.name}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Satır 2: Oyun tarzları */}
      <div className="player-badges-row player-badges-row-2">
        {styles.map(styleId => {
          const styleInfo = BADGES_CONFIG.styles.find(s => s.id === styleId);
          if (!styleInfo) return null;
          return (
            <StyleBadge
              key={styleId}
              info={styleInfo}
              isAdmin={isAdmin}
              onRemove={() => handleRemoveStyle(styleId)}
            />
          );
        })}
        {isAdmin && (
          <div className="player-badges-style-add">
            <button type="button" className="player-badges-add-btn" onClick={() => setShowStylePicker(!showStylePicker)} title="Oyun tarzı ekle">+</button>
            {showStylePicker && (
              <>
                <div className="player-badges-overlay" onClick={() => setShowStylePicker(false)} />
                <div className="player-badges-picker player-badges-picker-styles">
                  <button type="button" className="player-badges-back" onClick={() => setShowStylePicker(false)}>← Geri</button>
                  {BADGES_CONFIG.styles.length === 0 ? (
                    <p className="player-badges-empty">Henüz eklenmemiş.</p>
                  ) : (
                    BADGES_CONFIG.styles.filter(s => !styles.includes(s.id)).map(item => (
                      <button key={item.id} type="button" className="player-badges-picker-item" onClick={() => handleAddStyle(item.id)}>
                        <img src={item.image} alt={item.name} />
                        <span>{item.name}</span>
                      </button>
                    ))
                  )}
                  {BADGES_CONFIG.styles.filter(s => !styles.includes(s.id)).length === 0 && BADGES_CONFIG.styles.length > 0 && (
                    <p className="player-badges-empty">Tüm tarzlar eklendi.</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

const ChemistryBadge: React.FC<{ info: BadgeItem; isAdmin: boolean; onRemove: () => void }> = ({ info, isAdmin, onRemove }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={anchorRef}
      className="player-badge-item player-badge-item-chemistry"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => setShowTooltip((v) => !v)}
    >
      <img src={info.image} alt={info.name} />
      {isAdmin && (
        <button type="button" className="player-badge-remove" onClick={(e) => { e.stopPropagation(); onRemove(); }}>
          ×
        </button>
      )}
      <BadgeTooltipPortal anchorRef={anchorRef} show={showTooltip}>
        {info.name}
      </BadgeTooltipPortal>
    </div>
  );
};

const StyleBadge: React.FC<{ info: BadgeItem & { description: string }; isAdmin: boolean; onRemove: () => void }> = ({ info, isAdmin, onRemove }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={anchorRef}
      className="player-badge-item"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onClick={() => setShowTooltip((v) => !v)}
    >
      <img src={info.image} alt={info.name} />
      {isAdmin && <button type="button" className="player-badge-remove" onClick={(e) => { e.stopPropagation(); onRemove(); }}>×</button>}
      <BadgeTooltipPortal anchorRef={anchorRef} show={showTooltip}>
        <span className="player-badge-tooltip-name">{info.name}</span>
        <span className="player-badge-tooltip-desc">{info.description}</span>
      </BadgeTooltipPortal>
    </div>
  );
};
